import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { ArrowRight, Zap, Shield, Bell, Battery, Check } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERMISSIONS = [
  {
    id: 'accessibility',
    icon: Shield,
    color: '#7c3aed',
    title: 'Always know where you left off.',
    body: 'Enable auto-tracking and Qude remembers your spot on every show, every service, automatically.',
    note: 'On the next screen: tap "Installed apps" → tap Qude → toggle on. Only reads your streaming apps — never passwords.',
    buttonLabel: 'Enable Auto-Tracking',
    check: () => { try { return window.QudeAndroid?.isAccessibilityEnabled?.() === true; } catch { return false; } },
    enable: () => { try { window.QudeAndroid?.openAccessibilitySettings?.(); } catch {} },
  },
  {
    id: 'notifications',
    icon: Bell,
    color: '#4A9EFF',
    title: 'Catch every episode drop.',
    body: 'Enable notification access so Qude can detect what you\'re watching from streaming app alerts — automatically.',
    note: 'On the next screen find Qude and toggle it on. Qude only reads media playback notifications — never your personal messages.',
    buttonLabel: 'Enable Notification Access',
    check: () => { try { return window.QudeAndroid?.hasNotificationListenerEnabled?.() === true; } catch { return false; } },
    enable: () => { try { window.QudeAndroid?.requestNotificationListenerPermission?.(); } catch {} },
  },
  {
    id: 'battery',
    icon: Battery,
    color: '#10b981',
    title: 'Keep detection running.',
    body: 'Allow Qude to run in the background so it never misses what you\'re watching — even when the app is closed.',
    note: 'On the next screen find Qude and set battery usage to "Unrestricted". This uses minimal battery.',
    buttonLabel: 'Unrestrict Battery Usage',
    check: () => { try { return window.QudeAndroid?.needsBatteryOptimizationExemption?.() === false; } catch { return false; } },
    enable: () => { try { window.QudeAndroid?.requestBatteryOptimizationExemption?.(); } catch {} },
  },
  {
    id: 'usage_stats',
    icon: Shield,
    color: '#f97316',
    title: 'Fill in the gaps.',
    body: 'Allow Qude to see which streaming apps you open and for how long — so it never misses a session even when other detection methods are unavailable.',
    note: 'On the next screen find Qude and toggle on "Permit usage access". No personal data is ever read.',
    buttonLabel: 'Enable Usage Access',
    check: () => { try { return window.QudeAndroid?.hasUsageStatsPermission?.() === true; } catch { return false; } },
    enable: () => { try { window.QudeAndroid?.requestUsageStatsPermission?.(); } catch {} },
  },
];

const STREAMING_SERVICES = [
  { id: 'netflix',        name: 'Netflix',      color: '#E50914' },
  { id: 'hulu',           name: 'Hulu',         color: '#1CE783' },
  { id: 'disney_plus',    name: 'Disney+',      color: '#113CCF' },
  { id: 'hbo_max',        name: 'Max',          color: '#B535F6' },
  { id: 'amazon_prime',   name: 'Prime Video',  color: '#00A8E1' },
  { id: 'apple_tv',       name: 'Apple TV+',    color: '#bbbbbb' },
  { id: 'peacock',        name: 'Peacock',      color: '#FDB927' },
  { id: 'paramount_plus', name: 'Paramount+',   color: '#0064FF' },
];

// TOTAL_STEPS = welcome(1) + permissions(N) + services(1) + taste(1) + done(1)
const TOTAL_STEPS = PERMISSIONS.length + 4;

function PermissionStep({ permission, stepNum, onNext, onSkip, isAndroid }) {
  const [enabled, setEnabled] = useState(false);
  const Icon = permission.icon;

  const checkPermission = useCallback(() => {
    const result = permission.check();
    setEnabled(result);
    return result;
  }, [permission]);

  useEffect(() => {
    checkPermission();
    const interval = setInterval(checkPermission, 1000);
    const onFocus = () => setTimeout(checkPermission, 500);
    const onVisibility = () => {
      if (document.visibilityState === 'visible') setTimeout(checkPermission, 500);
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [checkPermission]);

  const handleEnable = () => {
    permission.enable();
    setTimeout(checkPermission, 1500);
    setTimeout(checkPermission, 3000);
  };

  return (
    <div className="space-y-6">
      <div>
        <p className="text-xs font-black tracking-widest text-violet-400 uppercase mb-1">
          Step {stepNum} of {TOTAL_STEPS}
        </p>
        <h2 className="text-2xl font-black text-white" style={{ fontFamily: 'Outfit' }}>
          {permission.title}
        </h2>
      </div>

      <div className="rounded-2xl border p-5 space-y-4"
        style={{ background: `${permission.color}0a`, borderColor: `${permission.color}30` }}>
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: `${permission.color}20`, border: `1px solid ${permission.color}40` }}>
            <Icon className="w-6 h-6" style={{ color: permission.color }} />
          </div>
          <p className="text-sm font-semibold text-white leading-snug">{permission.body}</p>
        </div>
        <p className="text-xs text-neutral-500 leading-relaxed">{permission.note}</p>
      </div>

      {enabled ? (
        <div className="flex items-center gap-3 p-4 rounded-2xl border border-emerald-500/30"
          style={{ background: 'rgba(16,185,129,0.08)' }}>
          <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0">
            <Check className="w-4 h-4 text-white" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-bold text-emerald-400">Enabled ✓</p>
            <p className="text-xs text-neutral-500">Qude is set up for full detection</p>
          </div>
          <button onClick={onNext}
            className="flex items-center gap-1 px-3 py-2 rounded-xl text-xs font-bold text-white"
            style={{ background: 'linear-gradient(135deg, #7c3aed, #4A9EFF)' }}>
            Next <ArrowRight className="w-3 h-3" />
          </button>
        </div>
      ) : (
        <>
          {isAndroid ? (
            <button onClick={handleEnable}
              className="w-full flex items-center justify-center gap-2 py-4 rounded-2xl font-bold text-white text-base"
              style={{ background: `linear-gradient(135deg, ${permission.color}, #7c3aed)`, fontFamily: 'Outfit' }}>
              <Icon className="w-5 h-5" />
              {permission.buttonLabel}
            </button>
          ) : (
            <div className="p-4 rounded-2xl border border-white/10 text-center">
              <p className="text-xs text-neutral-500">This permission is only needed on the Android app.</p>
            </div>
          )}
          <button onClick={onSkip}
            className="w-full py-3 rounded-2xl text-sm font-semibold text-neutral-400 border border-white/10 hover:text-white transition-colors">
            Skip for now →
          </button>
        </>
      )}
    </div>
  );
}

function ServicesStep({ stepNum, totalSteps, onNext }) {
  const { getToken } = useAuth();
  const [selected, setSelected] = useState(new Set());
  const [saving, setSaving] = useState(false);

  const toggle = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleContinue = async () => {
    setSaving(true);
    try {
      const token = await getToken();
      await Promise.all(
        [...selected].map(id =>
          axios.post(
            `${API}/services/connect/${id}`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          ).catch(() => {})
        )
      );
    } catch {}
    setSaving(false);
    onNext();
  };

  return (
    <div className="space-y-6">
      <div>
        <p className="text-xs font-black tracking-widest text-violet-400 uppercase mb-1">
          Step {stepNum} of {totalSteps}
        </p>
        <h2 className="text-2xl font-black text-white" style={{ fontFamily: 'Outfit' }}>
          Which services do you subscribe to?
        </h2>
        <p className="text-sm text-neutral-500 mt-2">
          Select all that apply. You can always add more later.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        {STREAMING_SERVICES.map(svc => {
          const isSelected = selected.has(svc.id);
          return (
            <button
              key={svc.id}
              onClick={() => toggle(svc.id)}
              className={`flex items-center gap-3 p-4 rounded-2xl border transition-all text-left ${
                isSelected
                  ? 'border-violet-500/50 bg-violet-600/10'
                  : 'border-white/8 bg-white/2 hover:border-white/15'
              }`}
            >
              <div className="w-3 h-3 rounded-full flex-shrink-0"
                   style={{ background: svc.color }} />
              <span className="text-sm font-semibold text-white">{svc.name}</span>
              {isSelected && (
                <Check className="w-4 h-4 text-violet-400 ml-auto flex-shrink-0" />
              )}
            </button>
          );
        })}
      </div>

      <button
        onClick={handleContinue}
        disabled={saving}
        className="w-full flex items-center justify-center gap-2 py-4 rounded-2xl font-bold text-white text-base disabled:opacity-60"
        style={{ background: 'linear-gradient(135deg, #7c3aed, #4A9EFF)', fontFamily: 'Outfit' }}
      >
        {saving ? 'Saving…' : <>Continue <ArrowRight className="w-5 h-5" /></>}
      </button>
      <button
        onClick={onNext}
        className="w-full py-3 rounded-2xl text-sm font-semibold text-neutral-400 border border-white/10 hover:text-white transition-colors"
      >
        Skip for now →
      </button>
    </div>
  );
}

// The one taste signal onboarding never captured before this -- every step
// above only ever asks about permissions/services, never what the person
// actually likes. Picking a handful of real titles beats any genre
// checklist for information-per-question (a title carries genre+tone+
// pacing+audience at once) and feeds backend/taste_seed.py directly, which
// server.py's MoodQ/Watch Next ranking already composes in alongside real
// viewing history -- so a brand-new account gets a real personalized
// recommendation on day one instead of generic TMDB popularity.
function TasteStep({ stepNum, totalSteps, onNext }) {
  const { getToken } = useAuth();
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await getToken();
        const res = await axios.get(`${API}/onboarding/taste-candidates`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setCandidates(res.data?.candidates || []);
      } catch {
        if (!cancelled) setCandidates([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [getToken]);

  const toggle = (tmdbId) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(tmdbId)) next.delete(tmdbId);
      else next.add(tmdbId);
      return next;
    });
  };

  const handleContinue = async () => {
    if (selected.size === 0) { onNext(); return; }
    setSaving(true);
    try {
      const token = await getToken();
      await axios.post(
        `${API}/onboarding/taste-seed`,
        { tmdb_ids: [...selected] },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch {}
    setSaving(false);
    onNext();
  };

  // No TMDB-backed candidates available (key not configured, or the fetch
  // failed) -- skip straight through rather than show an empty grid with
  // nothing to pick.
  if (!loading && candidates.length === 0) {
    onNext();
    return null;
  }

  return (
    <div className="space-y-6">
      <div>
        <p className="text-xs font-black tracking-widest text-violet-400 uppercase mb-1">
          Step {stepNum} of {totalSteps}
        </p>
        <h2 className="text-2xl font-black text-white" style={{ fontFamily: 'Outfit' }}>
          Pick a few shows you already love
        </h2>
        <p className="text-sm text-neutral-500 mt-2">
          This is how Qude personalizes your very first recommendations — before you've watched anything through the app.
        </p>
      </div>

      {loading ? (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="rounded-xl bg-white/5 animate-pulse" style={{ aspectRatio: '2/3' }} />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-[50vh] overflow-y-auto">
          {candidates.map(c => {
            const isSelected = selected.has(c.tmdb_id);
            return (
              <button
                key={c.tmdb_id}
                onClick={() => toggle(c.tmdb_id)}
                className={`relative rounded-xl overflow-hidden border-2 transition-all ${
                  isSelected ? 'border-violet-500' : 'border-transparent'
                }`}
                style={{ aspectRatio: '2/3' }}
              >
                {c.poster_url ? (
                  <img src={c.poster_url} alt={c.title} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-white/5 flex items-center justify-center text-xs text-neutral-500 p-2 text-center">
                    {c.title}
                  </div>
                )}
                {isSelected && (
                  <div className="absolute inset-0 bg-violet-600/30 flex items-center justify-center">
                    <Check className="w-8 h-8 text-white drop-shadow-lg" />
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}

      <button
        onClick={handleContinue}
        disabled={saving}
        className="w-full flex items-center justify-center gap-2 py-4 rounded-2xl font-bold text-white text-base disabled:opacity-60"
        style={{ background: 'linear-gradient(135deg, #7c3aed, #4A9EFF)', fontFamily: 'Outfit' }}
      >
        {saving ? 'Saving…' : selected.size > 0
          ? <>Continue with {selected.size} pick{selected.size === 1 ? '' : 's'} <ArrowRight className="w-5 h-5" /></>
          : <>Continue <ArrowRight className="w-5 h-5" /></>}
      </button>
      <button
        onClick={onNext}
        className="w-full py-3 rounded-2xl text-sm font-semibold text-neutral-400 border border-white/10 hover:text-white transition-colors"
      >
        Skip for now →
      </button>
    </div>
  );
}

export default function Onboarding() {
  const navigate = useNavigate();
  const { user, getToken } = useAuth();
  const [step, setStep] = useState(1);
  const [isAndroid, setIsAndroid] = useState(false);
  const [bridgeReady, setBridgeReady] = useState(false);

  // Detect Android bridge with retries
  useEffect(() => {
    const check = () => {
      if (window.QudeAndroid) {
        setIsAndroid(true);
        setBridgeReady(true);
        return true;
      }
      return false;
    };
    if (!check()) {
      const t1 = setTimeout(() => { check(); }, 500);
      const t2 = setTimeout(() => { check(); }, 1000);
      const t3 = setTimeout(() => { check(); setBridgeReady(true); }, 2000);
      return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
    }
  }, []);

  const handleFinish = () => {
    localStorage.setItem('qude_onboarded', '1');
    // Best-effort, fire-and-forget -- localStorage is still what gates the
    // very next navigation (instant, no network wait), this just makes sure
    // a new device/browser for the same account doesn't get sent through
    // onboarding again later (ProtectedRoute checks this server value when
    // localStorage is empty).
    getToken().then((token) => {
      if (!token) return;
      return axios.post(`${API}/user/onboarding-complete`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
    }).catch(() => {});
    navigate('/hub');
  };

  // Step layout (Android):
  // 1          = welcome
  // 2..N+1     = permission steps (PERMISSIONS.length steps)
  // N+2        = services step
  // N+3        = taste-seed step ("pick a few shows you like")
  // N+4 (last) = done
  //
  // The permission steps are all Android-WebView-bridge-only (each checks/
  // sets window.QudeAndroid.*) -- on the website every one of them used to
  // render real Android setup instructions followed by "this permission is
  // only needed on the Android app," forcing every web signup to click
  // Skip four times before reaching anything useful. Web users now skip
  // straight from Welcome to Services: 1 = welcome, 2 = services, 3 = taste, 4 = done.
  const servicesStep = isAndroid ? PERMISSIONS.length + 2 : 2;
  const tasteStep = isAndroid ? PERMISSIONS.length + 3 : 3;
  const doneStep = isAndroid ? PERMISSIONS.length + 4 : 4;
  const effectiveTotalSteps = isAndroid ? TOTAL_STEPS : 4;

  const permissionIndex = step - 2;
  const currentPermission = isAndroid && permissionIndex >= 0 && permissionIndex < PERMISSIONS.length
    ? PERMISSIONS[permissionIndex]
    : null;

  const firstName = user?.firstName || '';

  // Show loading until bridge detection has had a chance to run
  if (!bridgeReady) {
    return (
      <div style={{ minHeight: '100vh', background: '#050508', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ width: 32, height: 32, border: '2px solid #7c3aed', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6 py-12"
      style={{ background: '#050508' }}>
      <div className="w-full max-w-md">

        {/* Logo */}
        <div className="text-center mb-8">
          <img src="/logo.png" alt="Qude" style={{ width: 64, height: 64, margin: '0 auto 8px' }} />
          <p className="text-sm text-neutral-500">Let's get you set up</p>
        </div>

        {/* Progress bar */}
        <div className="flex items-center gap-1.5 mb-8">
          {Array.from({ length: effectiveTotalSteps }).map((_, i) => (
            <div key={i} className="flex-1 h-1.5 rounded-full overflow-hidden bg-white/10">
              <div className="h-full rounded-full transition-all duration-500"
                style={{
                  width: i + 1 <= step ? '100%' : '0%',
                  background: i + 1 <= step ? 'linear-gradient(90deg, #7c3aed, #4A9EFF)' : 'transparent',
                }} />
            </div>
          ))}
        </div>

        {/* ── Step 1: Welcome ── */}
        {step === 1 && (
          <div className="space-y-6">
            <div>
              <p className="text-xs font-black tracking-widest text-violet-400 uppercase mb-1">
                Step 1 of {effectiveTotalSteps}
              </p>
              <h2 className="text-2xl font-black text-white" style={{ fontFamily: 'Outfit' }}>
                Welcome{firstName ? `, ${firstName}` : ''} 👋
              </h2>
              <p className="text-sm text-neutral-500 mt-2">
                {isAndroid
                  ? "Qude tracks what you watch automatically across all your streaming services. Let's set up a few permissions so everything works perfectly."
                  : "Qude tracks what you watch automatically across all your streaming services. Let's get your services set up."}
              </p>
            </div>
            <div className="rounded-2xl border border-white/8 p-5 space-y-3"
              style={{ background: 'rgba(255,255,255,0.02)' }}>
              {(isAndroid ? [
                { icon: '🛡️', text: 'Auto-track shows as you watch' },
                { icon: '🔔', text: 'Detect playback from notifications' },
                { icon: '🔋', text: 'Keep running in the background' },
                { icon: '📊', text: 'Fill detection gaps with usage access' },
              ] : [
                { icon: '📺', text: 'One watchlist for all your streaming services' },
                { icon: '🎭', text: 'MoodQ picks the perfect show for how you feel' },
                { icon: '🔔', text: 'Drop alerts when new episodes air' },
              ]).map(item => (
                <div key={item.text} className="flex items-center gap-3">
                  <span className="text-lg flex-shrink-0">{item.icon}</span>
                  <p className="text-sm text-neutral-300">{item.text}</p>
                </div>
              ))}
            </div>
            <button onClick={() => setStep(2)}
              className="w-full flex items-center justify-center gap-2 py-4 rounded-2xl font-bold text-white text-base"
              style={{ background: 'linear-gradient(135deg, #7c3aed, #4A9EFF)', fontFamily: 'Outfit' }}>
              <ArrowRight className="w-5 h-5" /> Let's go
            </button>
          </div>
        )}

        {/* ── Permission steps ── */}
        {currentPermission && (
          <PermissionStep
            key={currentPermission.id}
            permission={currentPermission}
            stepNum={step}
            onNext={() => setStep(s => s + 1)}
            onSkip={() => setStep(s => s + 1)}
            isAndroid={isAndroid}
          />
        )}

        {/* ── Services step ── */}
        {step === servicesStep && (
          <ServicesStep
            stepNum={step}
            totalSteps={effectiveTotalSteps}
            onNext={() => setStep(s => s + 1)}
          />
        )}

        {/* ── Taste-seed step ── */}
        {step === tasteStep && (
          <TasteStep
            stepNum={step}
            totalSteps={effectiveTotalSteps}
            onNext={() => setStep(s => s + 1)}
          />
        )}

        {/* ── Final step: Done ── */}
        {step === doneStep && (
          <div className="space-y-6 text-center">
            <div>
              <p className="text-xs font-black tracking-widest text-violet-400 uppercase mb-1">
                All done!
              </p>
              <h2 className="text-2xl font-black text-white" style={{ fontFamily: 'Outfit' }}>
                You're all set! 🎉
              </h2>
              <p className="text-sm text-neutral-500 mt-2">
                Qude is ready. Start watching and it will track everything automatically.
              </p>
            </div>
            <div className="rounded-2xl border border-white/8 p-5 space-y-3 text-left"
              style={{ background: 'rgba(255,255,255,0.02)' }}>
              {[
                { icon: '📺', text: 'One watchlist for all your streaming services' },
                { icon: '⚡', text: 'Shows tracked automatically as you watch' },
                { icon: '🎭', text: 'MoodQ picks the perfect show for how you feel' },
                { icon: '🔔', text: 'Drop alerts when new episodes air' },
                { icon: '🛡️', text: 'Spoiler Shield keeps you safe' },
              ].map(item => (
                <div key={item.text} className="flex items-center gap-3">
                  <span className="text-lg flex-shrink-0">{item.icon}</span>
                  <p className="text-sm text-neutral-300">{item.text}</p>
                </div>
              ))}
            </div>
            <button onClick={handleFinish}
              className="w-full flex items-center justify-center gap-3 py-4 rounded-2xl font-black text-white text-base"
              style={{ background: 'linear-gradient(135deg, #7c3aed, #4A9EFF)', fontFamily: 'Outfit',
                       boxShadow: '0 0 40px rgba(124,58,237,0.3)' }}>
              <Zap className="w-5 h-5" /> Start Watching
            </button>
          </div>
        )}

        {/* Step dots */}
        <div className="flex justify-center gap-2 mt-8">
          {Array.from({ length: effectiveTotalSteps }).map((_, i) => (
            <div key={i} className={`h-2 rounded-full transition-all ${
              i + 1 === step ? 'bg-violet-400 w-4' : i + 1 < step ? 'bg-violet-600 w-2' : 'bg-white/20 w-2'
            }`} />
          ))}
        </div>
      </div>
    </div>
  );
}
